import React, { useState } from 'react';
import { User, Clock, Coffee, Utensils, Activity, Building2, ChevronDown, ChevronUp } from 'lucide-react';

const TeamMemberCard = ({
  member,
  session,
  activities,
  businessUnits,
  onActivityChange,
  onBusinessUnitChange,
  onBreakToggle,
  onLunchToggle
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isClockedIn = session?.clock_in_time && !session?.clock_out_time;
  const isOnBreak = session?.activity === 'BREAK' || session?.activity === 'Break';
  const isOnLunch = session?.activity === 'LUNCH' || session?.activity === 'Lunch';
  
  const getTimeSince = (timestamp) => {
    if (!timestamp) return 'N/A';
    const now = new Date();
    const start = new Date(timestamp);
    const diffMs = now - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  const getStatusColor = () => {
    if (!isClockedIn) return 'bg-gray-500';
    if (isOnBreak) return 'bg-yellow-500';
    if (isOnLunch) return 'bg-orange-500';
    return 'bg-green-500';
  };

  const getStatusText = () => {
    if (!isClockedIn) return 'Offline';
    if (isOnBreak) return 'On Break';
    if (isOnLunch) return 'On Lunch';
    return 'Active';
  };

  return (
    <div className={`bg-slate-700/50 border rounded-lg p-4 ${
      isClockedIn ? 'border-slate-600' : 'border-slate-700 opacity-75'
    }`}>
      <div 
        className="flex items-start justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center flex-1">
          <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center mr-3">
            <User className="w-5 h-5 text-slate-300" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-slate-200">{member.full_name || member.username}</h4>
            <div className="flex items-center text-sm text-slate-400 mb-1">
              <span className={`w-2 h-2 rounded-full mr-2 ${getStatusColor()}`}></span>
              {getStatusText()}
            </div>
            {isClockedIn && session && (
              <div className="flex flex-wrap gap-1 text-xs">
                {session.activity && session.activity !== 'IDLE' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-600/20 text-blue-300 border border-blue-600/30">
                    <Activity className="w-3 h-3 mr-1" />
                    {session.activity}
                  </span>
                )}
                {session.business_unit && session.business_unit !== 'IDLE' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-600/20 text-green-300 border border-green-600/30">
                    <Building2 className="w-3 h-3 mr-1" />
                    {session.business_unit}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center">
          {isClockedIn && (
            <div className="text-sm text-slate-400 mr-3">
              <Clock className="w-4 h-4 inline mr-1" />
              {getTimeSince(session.clock_in_time)}
            </div>
          )}
          {isExpanded ? 
            <ChevronUp className="w-5 h-5 text-slate-400" /> : 
            <ChevronDown className="w-5 h-5 text-slate-400" />
          }
        </div>
      </div>

      {isClockedIn && isExpanded && (
        <div className="mt-3">
          {/* Activity Selection */}
          <div className="mb-3">
            <label className="text-xs text-slate-400 flex items-center mb-1">
              <Activity className="w-3 h-3 mr-1" />
              Activity
            </label>
            <select
              value={session?.activity || ''}
              onChange={(e) => onActivityChange(member.employee_id, session.id, e.target.value)}
              className="w-full bg-slate-600 text-slate-200 text-sm rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!isClockedIn}
            >
              <option value="">Select Activity</option>
              {activities.filter(activity => activity.is_active).map(activity => (
                <option key={activity.id} value={activity.name}>
                  {activity.name}
                </option>
              ))}
            </select>
          </div>

          {/* Business Unit Selection */}
          <div className="mb-3">
            <label className="text-xs text-slate-400 flex items-center mb-1">
              <Building2 className="w-3 h-3 mr-1" />
              Business Unit
            </label>
            <select
              value={session?.business_unit || ''}
              onChange={(e) => onBusinessUnitChange(member.employee_id, session.id, e.target.value)}
              className="w-full bg-slate-600 text-slate-200 text-sm rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!isClockedIn}
            >
              <option value="">Select Business Unit</option>
              {businessUnits.filter(unit => unit.is_active).map(unit => (
                <option key={unit.id} value={unit.identifier}>
                  {unit.name}
                </option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onBreakToggle}
              className={`flex items-center justify-center px-3 py-1.5 rounded text-sm transition-colors ${
                isOnBreak
                  ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  : 'bg-slate-600 hover:bg-slate-500 text-slate-200'
              }`}
              disabled={!isClockedIn || isOnLunch}
            >
              <Coffee className="w-4 h-4 mr-1" />
              {isOnBreak ? 'End Break' : 'Start Break'}
            </button>
            
            <button
              onClick={onLunchToggle}
              className={`flex items-center justify-center px-3 py-1.5 rounded text-sm transition-colors ${
                isOnLunch
                  ? 'bg-orange-600 hover:bg-orange-700 text-white'
                  : 'bg-slate-600 hover:bg-slate-500 text-slate-200'
              }`}
              disabled={!isClockedIn || isOnBreak}
            >
              <Utensils className="w-4 h-4 mr-1" />
              {isOnLunch ? 'End Lunch' : 'Start Lunch'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMemberCard;